import React, { useState } from "react";
import MarkdownRenderer from "../../MarkdownRenderer";
import styles from "./answer-composer.module.scss";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

type AnswerComposerAgentProps = {
  parsedContent: any;
};

export default function AnswerComposerAgent({
  parsedContent,
}: AnswerComposerAgentProps): JSX.Element {
  const [showSqlQuery, setShowSqlQuery] = useState(false);

  const responseType = parsedContent?.finalAnswer?.response_type;
  const result = parsedContent?.finalAnswer?.result;
  const error = parsedContent?.finalAnswer?.error;

  // Handle error state
  if (responseType === "error" || error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorHeader}>
          <svg
            className={styles.errorIcon}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
              fill="currentColor"
            />
          </svg>
          <h3 className={styles.errorTitle}>Error</h3>
        </div>
        <div className={styles.errorMessage}>
          <MarkdownRenderer content={error || "An unexpected error occurred"} />
        </div>
      </div>
    );
  }

  // Handle success state
  if (responseType === "success" && result) {
    const {
      markdown_response,
      summary,
      sql_query,
      data_points_count,
      has_visualization_suggestion,
      visualization_type,
    } = result;

    return (
      <div className={styles.composerContainer}>
        {/* Main Response */}
        {markdown_response && (
          <div className={styles.mainResponse}>
            <MarkdownRenderer content={markdown_response} />
          </div>
        )}

        {/* Metadata Section */}
        <div className={styles.metadataSection}>
          <div className={styles.metadataGrid}>
            {data_points_count !== undefined && (
              <div className={styles.metadataItem}>
                <span className={styles.metadataLabel}>Data Points:</span>
                <span className={styles.metadataValue}>{data_points_count}</span>
              </div>
            )}
          </div>
        </div>

        {/* SQL Query Section */}
        {sql_query && (
          <div className={styles.sqlSection}>
            <div
              className={styles.sqlHeader}
              onClick={() => setShowSqlQuery(!showSqlQuery)}
            >
              {showSqlQuery ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              <span>SQL Query</span>
            </div>
            {showSqlQuery && (
              <div className={styles.sqlContent}>
                <pre>
                  <code>{sql_query}</code>
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Fallback for unknown response types
  return (
    <div className={styles.composerContainer}>
      <div className={styles.mainResponse}>
        <p>Unable to display response. Please try again.</p>
      </div>
    </div>
  );
}
